import { useMemo } from "react";

import { type ITemplateSelectionsState } from "commonComponents/utilities/MainNavigation/MainNavigation.types";

import { type ISelectionListContainerHandler } from "entity/selection/SelectionListContainer/SelectionListContainer.api.handler";
import { useSelectionConfig } from "entity/selection/SelectionListContainer/useSelectionConfig";

/**
 * Resolves the current template's Selections experience for nav visibility decisions.
 *
 * Outside template mode this stays inert — no fetch, nothing to gate on — because the per-template
 * signal only changes what template mode shows. Inside template mode it reuses the same jobId-keyed
 * config hook the Selections routes use, so a template switch invalidates the previous template's
 * answer in the same render rather than briefly showing the wrong nav.
 *
 * @param jobId The selected template's job id, or null when there's no valid selection
 * @param isTemplateMode Whether the app is currently in template mode
 * @param isSelectionsReimaginedBuilder Whether the builder has the SRI flag on at all
 * @param handler Injectable config handler; defaults to the real one inside useSelectionConfig
 */
export function useTemplateSelectionsState(
    jobId: number | null,
    isTemplateMode: boolean,
    isSelectionsReimaginedBuilder: boolean,
    handler?: ISelectionListContainerHandler,
): ITemplateSelectionsState {
    // Only fetch when the answer can actually vary: inside template mode, on a builder running SRI.
    // Everywhere else no template can be reimagined, so the config would tell us nothing.
    const shouldResolve = isTemplateMode && isSelectionsReimaginedBuilder;

    // Sentinels (AllJobs = 0, NoJobs = -1) mean no single template is selected, which is the
    // default on entering template mode. There is no template to be reimagined, so answer "not SRI"
    // rather than leaving it unknown — otherwise the nav item vanishes until one is picked.
    const hasSingleTemplate = jobId !== null && jobId > 0;
    const { config } = useSelectionConfig(
        shouldResolve && hasSingleTemplate ? jobId : null,
        handler,
    );

    // Stable reference: callers feed this into useMemo deps for the whole nav tree, and a fresh
    // object each render would rebuild it every time
    return useMemo(() => {
        if (!shouldResolve || !hasSingleTemplate) {
            return { isSelectionsReimaginedTemplate: false };
        }

        // Stays undefined while loading or after a failed load. Collapsing that to false would read
        // as "legacy template" and show the nav item for templates that may well be SRI.
        return { isSelectionsReimaginedTemplate: config?.isSelectionsReimaginedJob };
    }, [config, shouldResolve, hasSingleTemplate]);
}
